import { computed, type InjectionKey, type ComputedRef, ref, type Ref, watch } from 'vue'

import { apiFetch } from '@/services/api'

export const datePresetOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
]

const VALID_PRESETS = new Set(datePresetOptions.map((o) => o.value))
const STORAGE_KEY = 'raven:reports:filters'

interface StoredFilters {
  datePreset?: string
  salesPlayId?: string
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export interface ReportsFilterState {
  datePreset: Ref<string>
  customStart: Ref<string>
  customEnd: Ref<string>
  dateRange: ComputedRef<{ startDate: string; endDate: string }>
  salesPlayId: Ref<string>
  salesPlays: Ref<{ id: string; name: string }[]>
  salesPlayOptions: ComputedRef<{ value: string; label: string }[]>
}

export const REPORTS_FILTER_KEY: InjectionKey<ReportsFilterState> = Symbol('reportsFilters')

function loadFilters(): StoredFilters {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* noop */ }
  return {}
}

function saveFilters(filters: StoredFilters) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)) } catch { /* noop */ }
}

export function useReportsFilters(): ReportsFilterState {
  const stored = loadFilters()
  const datePreset = ref(stored.datePreset && VALID_PRESETS.has(stored.datePreset) ? stored.datePreset : 'last_30_days')
  const customStart = ref('')
  const customEnd = ref('')

  // ── Sales play filter (shared across tabs, fetched once) ──────────
  const salesPlayId = ref('')
  const salesPlays = ref<{ id: string; name: string }[]>([])
  const salesPlayOptions = computed(() =>
    salesPlays.value.map((p) => ({ value: p.id, label: p.name }))
  )

  apiFetch('/sales-plays')
    .then((resp: any) => {
      salesPlays.value = (resp.plays || []).map((p: any) => ({ id: p.id, name: p.name }))
      // Restore stored selection only if it still exists in the list
      if (stored.salesPlayId && salesPlays.value.some((p) => p.id === stored.salesPlayId)) {
        salesPlayId.value = stored.salesPlayId
      }
    })
    .catch(() => { /* non-critical */ })

  watch([datePreset, salesPlayId], () => {
    saveFilters({ datePreset: datePreset.value, salesPlayId: salesPlayId.value })
  })

  const dateRange = computed<{ startDate: string; endDate: string }>(() => {
    const now = new Date()
    const today = startOfDay(now)

    switch (datePreset.value) {
      case 'today':
        return { startDate: today.toISOString(), endDate: now.toISOString() }

      case 'yesterday': {
        const y = new Date(today)
        y.setDate(y.getDate() - 1)
        return { startDate: y.toISOString(), endDate: today.toISOString() }
      }

      case 'last_7_days': {
        const d = new Date(today)
        d.setDate(d.getDate() - 7)
        return { startDate: d.toISOString(), endDate: now.toISOString() }
      }

      case 'this_week': {
        const dow = today.getDay()
        const mon = new Date(today)
        mon.setDate(mon.getDate() - ((dow + 6) % 7))
        return { startDate: mon.toISOString(), endDate: now.toISOString() }
      }

      case 'last_week': {
        const dow = today.getDay()
        const thisMon = new Date(today)
        thisMon.setDate(thisMon.getDate() - ((dow + 6) % 7))
        const lastMon = new Date(thisMon)
        lastMon.setDate(lastMon.getDate() - 7)
        return { startDate: lastMon.toISOString(), endDate: thisMon.toISOString() }
      }

      case 'last_30_days': {
        const d = new Date(today)
        d.setDate(d.getDate() - 30)
        return { startDate: d.toISOString(), endDate: now.toISOString() }
      }

      case 'this_month': {
        const m = new Date(today.getFullYear(), today.getMonth(), 1)
        return { startDate: m.toISOString(), endDate: now.toISOString() }
      }

      case 'last_month': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const end = new Date(today.getFullYear(), today.getMonth(), 1)
        return { startDate: start.toISOString(), endDate: end.toISOString() }
      }

      case 'this_quarter': {
        const qStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)
        return { startDate: qStart.toISOString(), endDate: now.toISOString() }
      }

      case 'last_90_days': {
        const d = new Date(today)
        d.setDate(d.getDate() - 90)
        return { startDate: d.toISOString(), endDate: now.toISOString() }
      }

      case 'custom': {
        const s = customStart.value ? new Date(customStart.value + 'T00:00:00') : today
        const e = customEnd.value ? new Date(customEnd.value + 'T23:59:59') : now
        return { startDate: s.toISOString(), endDate: e.toISOString() }
      }

      default:
        return { startDate: today.toISOString(), endDate: now.toISOString() }
    }
  })

  return { datePreset, customStart, customEnd, dateRange, salesPlayId, salesPlays, salesPlayOptions }
}
